/**
 * TrainingVideoLibrary - Visual folder structure for pre-analyzed training videos
 *
 * Shows local training videos with cached analysis results.
 * When a video is "analyzed", displays matrix rain animation for 11 seconds,
 * then reveals the pre-generated analysis results.
 *
 * @module v2/analyzer/TrainingVideoLibrary
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FolderOpen,
  Video,
  Play,
  Pause,
  Zap,
  ChevronRight,
  Database,
  Clock,
  AlertTriangle,
  CheckCircle,
  XCircle
} from 'lucide-react';

// Training video manifest
const PITCH_TYPES = ['Fastball', 'Curveball', 'Slider', 'Changeup'];
const CAMERA_ANGLES = [
  { id: 'HP', label: 'Home Plate', color: 'yellow' },
  { id: 'CF', label: 'Center Field', color: 'green' },
  { id: '1B', label: 'First Base', color: 'yellow' },
  { id: '3B', label: 'Third Base', color: 'yellow' },
];

const PITCH_COLORS = {
  Fastball: { bg: 'from-[#E8B800] to-[#D4A800]', text: 'text-[#E8B800]', border: 'border-[#E8B800]/50' },
  Curveball: { bg: 'from-[#C49B00] to-[#E8B800]', text: 'text-[#E8B800]', border: 'border-[#E8B800]/50' },
  Slider: { bg: 'from-[#E8B800] to-[#F0CC33]', text: 'text-[#E8B800]', border: 'border-[#E8B800]/50' },
  Changeup: { bg: 'from-green-500 to-emerald-500', text: 'text-green-400', border: 'border-green-500/50' },
};

/**
 * MatrixAnalysisAnimation - Full-screen matrix rain during analysis
 */
const MatrixAnalysisAnimation = ({ duration = 11000, onComplete, videoName }) => {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const startTimeRef = useRef(null);
  const [progress, setProgress] = useState(0);

  const characters = [
    'FB', 'SL', 'CB', 'CH', 'CT', 'SI', 'KC',
    'MPH', 'RPM', 'SPIN', 'VB', 'HB',
    '95', '96', '97', '98', '99', '100',
    'TELL', 'TIP', 'PATTERN', 'SLOT', 'GRIP',
    'DETECTING', 'ANALYZING', 'SCANNING',
    '\u26BE', '\u2605', '\u25B2', '\u25BC',
    '0', '1', '2', '3', '4', '5', '6', '7', '8', '9',
  ];

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    const fontSize = 16;
    const columns = Math.floor(canvas.width / fontSize);
    const drops = Array(columns).fill(0).map(() => Math.random() * -100);
    const speeds = Array(columns).fill(0).map(() => 0.3 + Math.random() * 0.7);
    const chars = Array(columns).fill('').map(() =>
      characters[Math.floor(Math.random() * characters.length)]
    );

    startTimeRef.current = Date.now();

    const draw = () => {
      const elapsed = Date.now() - startTimeRef.current;
      const prog = Math.min(elapsed / duration, 1);
      setProgress(prog);

      ctx.fillStyle = 'rgba(0, 0, 0, 0.05)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      const hue = 45 + (prog * 30);
      const saturation = 100;
      const lightness = 50 + (prog * 10);

      for (let i = 0; i < drops.length; i++) {
        if (Math.random() > 0.98) {
          chars[i] = characters[Math.floor(Math.random() * characters.length)];
        }

        const x = i * fontSize;
        const y = drops[i] * fontSize;

        if (Math.random() > 0.99) {
          ctx.shadowColor = `hsl(${hue}, ${saturation}%, ${lightness}%)`;
          ctx.shadowBlur = 20;
        } else {
          ctx.shadowBlur = 0;
        }

        const brightness = 0.3 + Math.random() * 0.7;
        ctx.fillStyle = `hsla(${hue}, ${saturation}%, ${lightness}%, ${brightness})`;
        ctx.font = `bold ${fontSize}px monospace`;
        ctx.fillText(chars[i], x, y);

        drops[i] += speeds[i];

        if (y > canvas.height && Math.random() > 0.975) {
          drops[i] = 0;
          speeds[i] = 0.3 + Math.random() * 0.7;
        }
      }

      if (prog < 1) {
        animationRef.current = requestAnimationFrame(draw);
      } else {
        onComplete?.();
      }
    };

    draw();

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [duration, onComplete]);

  const statusText = progress < 0.2
    ? 'INITIALIZING PITCH DETECTION...'
    : progress < 0.4
    ? 'LOADING CACHED ANALYSIS...'
    : progress < 0.6
    ? 'ANALYZING DELIVERY MECHANICS...'
    : progress < 0.8
    ? 'IDENTIFYING PITCH TELLS...'
    : 'GENERATING INTELLIGENCE REPORT...';

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100]"
      style={{ top: 0, left: 0, right: 0, bottom: 0 }}
    >
      <canvas ref={canvasRef} className="absolute inset-0" style={{ background: 'black' }} />

      {/* Overlay UI */}
      <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
        {/* Video being analyzed */}
        <div className="mb-8 text-center">
          <div className="text-[#E8B800] text-sm font-mono mb-2">ANALYZING VIDEO</div>
          <div className="text-white text-2xl font-bold">{videoName}</div>
        </div>

        {/* Progress bar */}
        <div className="w-96 max-w-[80%]">
          <div className="h-1 bg-white/10 rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-gradient-to-r from-[#E8B800] to-[#E8B800]"
              style={{ width: `${progress * 100}%` }}
            />
          </div>
          <div className="mt-4 text-center">
            <div className="text-[#E8B800] font-mono text-sm">{statusText}</div>
            <div className="text-white/60 font-mono text-xs mt-1">{Math.floor(progress * 100)}%</div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

/**
 * VideoCard - Individual video in the folder structure
 */
const VideoCard = ({ video, analysis, isSelected, onSelect, onAnalyze }) => {
  const videoRef = useRef(null);
  const [isHovered, setIsHovered] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);

  const togglePlay = (e) => {
    e.stopPropagation();
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const getTippingColor = () => {
    if (!analysis?.tippingDetected) return 'text-green-400';
    if (analysis.confidence > 0.7) return 'text-red-400';
    if (analysis.confidence > 0.5) return 'text-[#E8B800]';
    return 'text-[#E8B800]';
  };

  const getTippingIcon = () => {
    if (!analysis?.tippingDetected) return <CheckCircle className="w-4 h-4" />;
    if (analysis.confidence > 0.7) return <AlertTriangle className="w-4 h-4" />;
    return <AlertTriangle className="w-4 h-4" />;
  };

  return (
    <motion.div
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={() => onSelect(video)}
      className={`relative rounded-[14px] overflow-hidden cursor-pointer transition-all duration-200 ${
        isSelected
          ? 'ring-2 ring-[#E8B800]'
          : 'ring-1 ring-white/10 hover:ring-white/30'
      } bg-[#0A0A0A]`}
    >
      {/* Video Preview */}
      <div className="relative aspect-video bg-black">
        <video
          ref={videoRef}
          src={video.path}
          className="w-full h-full object-cover"
          muted
          loop
          playsInline
          onEnded={() => setIsPlaying(false)}
        />

        {/* Pitch Type Badge */}
        <div className={`absolute top-2 left-2 px-2 py-1 rounded-lg text-xs font-bold text-white bg-gradient-to-r ${PITCH_COLORS[video.pitchType].bg}`}>
          {video.pitchType}
        </div>

        {/* Camera Angle Badge */}
        <div className="absolute top-2 right-2 px-2 py-1 rounded-lg text-xs font-medium bg-black/60 text-[#999999]">
          {video.angleLabel}
        </div>

        {/* Tipping Status Indicator */}
        {analysis && (
          <div className={`absolute bottom-2 left-2 px-2 py-1 rounded-lg text-xs font-medium flex items-center gap-1 bg-black/70 ${getTippingColor()}`}>
            {getTippingIcon()}
            {analysis.tippingDetected ? `${Math.round(analysis.confidence * 100)}% Tell` : 'Clean'}
          </div>
        )}

        {/* Play/Pause Overlay */}
        {isHovered && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="absolute inset-0 bg-black/40 flex items-center justify-center"
          >
            <button
              onClick={togglePlay}
              className="w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center hover:bg-white/30 transition-colors"
            >
              {isPlaying ? (
                <Pause className="w-6 h-6 text-white" />
              ) : (
                <Play className="w-6 h-6 text-white ml-1" />
              )}
            </button>
          </motion.div>
        )}

        {/* Cached indicator */}
        <div className="absolute bottom-2 right-2">
          <div className={`w-2 h-2 rounded-full ${
            analysis ? 'bg-green-500' : 'bg-[#555555]'
          }`} title={analysis ? 'Cached analysis available' : 'No cached analysis'} />
        </div>
      </div>

      {/* Video Info */}
      <div className="p-3 bg-[#0A0A0A]">
        <div className="text-sm font-medium text-[#F5F5F5]">
          {video.pitchType} - {video.angleLabel}
        </div>
        <div className="text-xs mt-1 text-[#999999]">
          {video.filename}
        </div>

        {/* Analyze Button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onAnalyze(video);
          }}
          className="mt-3 w-full py-2 rounded-lg bg-gradient-to-r from-[#E8B800] to-[#D4A800] text-black text-sm font-medium flex items-center justify-center gap-2 hover:from-[#D4A800] hover:to-[#C49B00] transition-all"
        >
          <Zap className="w-4 h-4" />
          Analyze
        </button>
      </div>
    </motion.div>
  );
};

/**
 * AnalysisResultPanel - Shows cached analysis results
 */
const AnalysisResultPanel = ({ video, analysis, onClose }) => {
  if (!analysis) return null;

  const getTippingLevelColor = () => {
    if (!analysis.tippingDetected) return 'text-green-400 bg-green-500/20';
    if (analysis.confidence > 0.7) return 'text-red-400 bg-red-500/20';
    if (analysis.confidence > 0.5) return 'text-[#E8B800] bg-[#E8B800]/20';
    return 'text-[#E8B800] bg-[#E8B800]/20';
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 20 }}
      className="rounded-[14px] overflow-hidden bg-[#0A0A0A] border border-[#1A1A1A]"
    >
      {/* Header */}
      <div className="p-4 border-b border-[#1A1A1A]">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center bg-gradient-to-r ${PITCH_COLORS[video.pitchType].bg}`}>
              <Video className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-[#F5F5F5]">
                {video.pitchType} Analysis
              </h3>
              <p className="text-sm text-[#999999]">
                {video.angleLabel} Camera
              </p>
            </div>
          </div>
          <div className={`px-3 py-1.5 rounded-lg font-medium ${getTippingLevelColor()}`}>
            {analysis.tippingDetected ? `${Math.round(analysis.confidence * 100)}% Confidence` : 'No Tipping'}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-4 space-y-4">
        {/* Tipping Status */}
        <div className={`p-4 rounded-[14px] ${
          analysis.tippingDetected
            ? 'bg-red-500/10 border border-red-500/30'
            : 'bg-green-500/10 border border-green-500/30'
        }`}>
          <div className="flex items-start gap-3">
            {analysis.tippingDetected ? (
              <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            ) : (
              <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
            )}
            <div>
              <h4 className={`font-medium ${
                analysis.tippingDetected ? 'text-red-400' : 'text-green-400'
              }`}>
                {analysis.tippingDetected ? 'Advanced Scouting Detected' : 'Clean Delivery'}
              </h4>
              <p className="text-sm mt-1 text-[#999999]">
                {analysis.analysisNotes}
              </p>
            </div>
          </div>
        </div>

        {/* Indicators */}
        {analysis.indicators && analysis.indicators.length > 0 && (
          <div>
            <h4 className="font-medium mb-3 text-[#F5F5F5]">
              Detected Tells ({analysis.indicators.length})
            </h4>
            <div className="space-y-3">
              {analysis.indicators.map((indicator, idx) => (
                <div
                  key={idx}
                  className="p-4 rounded-[14px] bg-[#111111]"
                >
                  <div className="flex items-start justify-between mb-2">
                    <span className="px-2 py-0.5 rounded text-xs font-bold bg-[#E8B800]/20 text-[#E8B800]">
                      {indicator.categoryLabel}
                    </span>
                    <span className="text-xs text-[#999999]">
                      {indicator.timestamp && `@ ${indicator.timestamp}`}
                    </span>
                  </div>
                  <h5 className="font-medium text-[#F5F5F5]">
                    {indicator.tell}
                  </h5>
                  <p className="text-sm mt-1 text-[#999999]">
                    {indicator.whatToLookFor}
                  </p>
                  <div className="mt-2 flex items-center gap-2">
                    <div className="flex-1 h-1.5 bg-[#222222] rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-[#E8B800] to-red-500"
                        style={{ width: `${indicator.confidence * 100}%` }}
                      />
                    </div>
                    <span className="text-xs text-[#E8B800] font-medium">
                      {Math.round(indicator.confidence * 100)}%
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Hitter Advantage */}
        <div className="p-4 rounded-[14px] bg-[#E8B800]/10 border border-[#E8B800]/30">
          <h4 className="font-medium mb-2 flex items-center gap-2 text-[#E8B800]">
            <Zap className="w-4 h-4" />
            Hitter Advantage
          </h4>
          <p className="text-sm text-[#999999]">
            {analysis.hitterAdvantage}
          </p>
        </div>
      </div>
    </motion.div>
  );
};

/**
 * WakeForestReportPanel - Full comprehensive report display for MoE mode
 */
const WakeForestReportPanel = ({ report, onClose }) => {
  if (!report) return null;

  const getTippingColor = (rate) => {
    if (rate >= 0.75) return 'text-red-400 bg-red-500/20';
    if (rate >= 0.5) return 'text-[#E8B800] bg-[#E8B800]/20';
    if (rate > 0) return 'text-[#E8B800] bg-[#E8B800]/20';
    return 'text-green-400 bg-green-500/20';
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 20 }}
      className="rounded-[14px] overflow-hidden bg-[#0A0A0A] border border-[#1A1A1A]"
    >
      {/* Header */}
      <div className="p-4 border-b border-[#1A1A1A] bg-gradient-to-r from-red-900/30 to-[#0A0A0A]">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold text-[#F5F5F5]">
              {report.pitcher} Intelligence Report
            </h3>
            <p className="text-sm text-[#999999]">
              {report.team} • MoE Video Analysis
            </p>
          </div>
          <div className={`px-3 py-1.5 rounded-lg font-bold ${getTippingColor(report.threatAssessment?.tippingRate || 0)}`}>
            {report.threatAssessment?.level} THREAT
          </div>
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="max-h-[600px] overflow-y-auto p-4 space-y-4">
        {/* Threat Assessment */}
        <div className="p-4 rounded-[14px] bg-red-500/10 border border-red-500/30">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0" />
            <div>
              <h4 className="font-bold text-red-400">
                {report.threatAssessment?.headline}
              </h4>
              <p className="text-sm mt-2 text-[#999999]">
                {report.threatAssessment?.quickRead}
              </p>
              <div className="flex items-center gap-4 mt-3 text-xs">
                <span className="text-red-400 font-bold">
                  {Math.round((report.threatAssessment?.tippingRate || 0) * 100)}% Tipping Rate
                </span>
                <span className="text-[#555555]">
                  {report.threatAssessment?.tippingDetected}/{report.threatAssessment?.totalAnalyzed} videos
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Primary Tell */}
        {report.primaryTell && (
          <div className="p-4 rounded-[14px] bg-[#E8B800]/10 border border-[#E8B800]/30">
            <h4 className="font-bold mb-2 flex items-center gap-2 text-[#E8B800]">
              <Zap className="w-4 h-4" />
              PRIMARY TELL - {report.primaryTell.pitchRevealed}
            </h4>
            <p className="text-sm text-[#999999]">
              {report.primaryTell.whatToLookFor}
            </p>
            <div className="mt-2 flex items-center gap-2 text-xs">
              <span className="text-[#999999]">
                {report.primaryTell.whenItHappens}
              </span>
              <span className="text-[#E8B800] font-medium">
                {report.primaryTell.confidence}
              </span>
            </div>
          </div>
        )}

        {/* Secondary Tells */}
        {report.secondaryTells && report.secondaryTells.length > 0 && (
          <div>
            <h4 className="font-medium mb-3 text-[#F5F5F5]">
              Secondary Tells ({report.secondaryTells.length})
            </h4>
            <div className="grid grid-cols-1 gap-2">
              {report.secondaryTells.map((tell, idx) => (
                <div key={idx} className="p-3 rounded-lg bg-[#111111]">
                  <div className="flex items-start justify-between">
                    <span className={`px-2 py-0.5 rounded text-xs font-bold ${
                      tell.pitchRevealed === 'CB' ? 'bg-blue-500/20 text-blue-400' :
                      tell.pitchRevealed === 'SL' ? 'bg-purple-500/20 text-purple-400' :
                      tell.pitchRevealed === 'CH' ? 'bg-green-500/20 text-green-400' :
                      'bg-red-500/20 text-red-400'
                    }`}>
                      {tell.pitchRevealed}
                    </span>
                  </div>
                  <p className="text-sm mt-2 text-[#999999]">
                    {tell.whatToLookFor}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Pitch Scouting Grid */}
        {report.pitchScouting && (
          <div>
            <h4 className="font-medium mb-3 text-[#F5F5F5]">
              Pitch-by-Pitch Breakdown
            </h4>
            <div className="grid grid-cols-2 gap-3">
              {Object.entries(report.pitchScouting).map(([pitchType, data]) => (
                <div key={pitchType} className="p-3 rounded-lg bg-[#111111]">
                  <div className="flex items-center justify-between mb-2">
                    <span className={`font-bold ${
                      pitchType === 'CB' ? 'text-blue-400' :
                      pitchType === 'SL' ? 'text-purple-400' :
                      pitchType === 'CH' ? 'text-green-400' :
                      'text-red-400'
                    }`}>
                      {pitchType === 'FB' ? 'Fastball' :
                       pitchType === 'CB' ? 'Curveball' :
                       pitchType === 'SL' ? 'Slider' : 'Changeup'}
                    </span>
                    <span className={`text-xs font-bold ${getTippingColor(data.tippingRate)}`}>
                      {Math.round(data.tippingRate * 100)}%
                    </span>
                  </div>
                  <div className="h-1.5 bg-[#222222] rounded-full overflow-hidden">
                    <div
                      className={`h-full ${
                        data.tippingRate >= 0.75 ? 'bg-red-500' :
                        data.tippingRate >= 0.5 ? 'bg-[#E8B800]' :
                        data.tippingRate > 0 ? 'bg-[#E8B800]' : 'bg-green-500'
                      }`}
                      style={{ width: `${data.tippingRate * 100}%` }}
                    />
                  </div>
                  <div className="text-xs text-[#555555] mt-1">
                    {data.tippedCount}/{data.totalSeen} tipped
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* At-Bat Strategy */}
        {report.atBatStrategy && (
          <div className="p-4 rounded-[14px] bg-green-500/10 border border-green-500/30">
            <h4 className="font-bold mb-3 text-green-400">
              At-Bat Strategy Guide
            </h4>
            <div className="space-y-3 text-sm">
              <div>
                <span className="font-medium text-[#F5F5F5]">First Pitch:</span>
                <p className="text-[#999999]">{report.atBatStrategy.firstPitch}</p>
              </div>
              <div>
                <span className="font-medium text-[#F5F5F5]">Ahead in Count:</span>
                <p className="text-[#999999]">{report.atBatStrategy.aheadInCount}</p>
              </div>
              <div>
                <span className="font-medium text-[#F5F5F5]">Behind in Count:</span>
                <p className="text-[#999999]">{report.atBatStrategy.behindInCount}</p>
              </div>
              <div>
                <span className="font-medium text-[#F5F5F5]">Two Strikes:</span>
                <p className="text-[#999999]">{report.atBatStrategy.twoStrikes}</p>
              </div>
            </div>
          </div>
        )}

        {/* High Value Tells */}
        {report.highValueTells && report.highValueTells.length > 0 && (
          <div>
            <h4 className="font-medium mb-3 text-[#F5F5F5]">
              High-Value Tells ({report.highValueTells.length})
            </h4>
            <div className="space-y-3">
              {report.highValueTells.slice(0, 3).map((tell, idx) => (
                <div key={idx} className="p-4 rounded-[14px] bg-[#111111]">
                  <div className="flex items-start justify-between mb-2">
                    <span className="px-2 py-0.5 rounded text-xs font-bold bg-[#E8B800]/20 text-[#E8B800]">
                      {tell.categoryLabel}
                    </span>
                    <span className="text-xs text-[#999999]">
                      → {tell.pitchRevealed}
                    </span>
                  </div>
                  <h5 className="font-medium text-[#F5F5F5]">
                    {tell.tell}
                  </h5>
                  <p className="text-sm mt-1 text-[#999999]">
                    {tell.whatToLookFor}
                  </p>
                  <div className="mt-2 flex items-center gap-2">
                    <div className="flex-1 h-1.5 bg-[#222222] rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-[#E8B800] to-red-500"
                        style={{ width: `${tell.confidence * 100}%` }}
                      />
                    </div>
                    <span className="text-xs text-[#E8B800] font-medium">
                      {Math.round(tell.confidence * 100)}%
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
};

/**
 * TrainingVideoLibrary - Main component
 * @param {Object} props
 * @param {Function} [props.onSelectForMoE] - Callback when video selected for MoE analysis
 * @param {string} [props.mode='individual'] - 'individual' for single video analysis, 'moe' for full report
 */
export const TrainingVideoLibrary = ({ onSelectForMoE, mode = 'individual' }) => {
  const [cachedAnalyses, setCachedAnalyses] = useState(null);
  const [fullReport, setFullReport] = useState(null);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [analyzingVideo, setAnalyzingVideo] = useState(null);
  const [showAnalysisResult, setShowAnalysisResult] = useState(false);
  const [currentAnalysis, setCurrentAnalysis] = useState(null);
  const [expandedPitchType, setExpandedPitchType] = useState('Fastball');
  const [isLoading, setIsLoading] = useState(true);

  // Generate all videos
  const allVideos = React.useMemo(() => {
    const videos = [];
    PITCH_TYPES.forEach(pitch => {
      CAMERA_ANGLES.forEach(angle => {
        videos.push({
          id: `${pitch}_${angle.id}`,
          pitchType: pitch,
          angle: angle.id,
          angleLabel: angle.label,
          filename: `${pitch}_${angle.id}.mov`,
          path: `/training/pitch-videos/${pitch}_${angle.id}.mov`,
        });
      });
    });
    return videos;
  }, []);

  // Load cached analyses and full report
  useEffect(() => {
    const loadData = async () => {
      try {
        // Load individual video analyses
        const analysisResponse = await fetch('/training/pitch-videos/cached-analysis.json');
        const analysisData = await analysisResponse.json();
        setCachedAnalyses(analysisData);

        // Load full Wake Forest report for MoE mode
        if (mode === 'moe') {
          const reportResponse = await fetch('/intel/wake-forest/wake-forest-pitcher.json');
          const reportData = await reportResponse.json();
          setFullReport(reportData);
        }
      } catch (error) {
        console.error('Failed to load cached data:', error);
      } finally {
        setIsLoading(false);
      }
    };
    loadData();
  }, [mode]);

  // Handle video analysis
  const handleAnalyze = useCallback((video) => {
    setAnalyzingVideo(video);
    setShowAnalysisResult(false);
  }, []);

  // Handle analysis complete
  const handleAnalysisComplete = useCallback(() => {
    if (analyzingVideo) {
      if (mode === 'moe' && fullReport) {
        // MoE mode: Show full Wake Forest report
        setCurrentAnalysis(fullReport);
        setSelectedVideo(analyzingVideo);
        setShowAnalysisResult(true);
      } else if (cachedAnalyses?.videoAnalyses) {
        // Individual mode: Show individual video analysis
        const analysisKey = `${analyzingVideo.pitchType}_${analyzingVideo.angle}`;
        const analysis = cachedAnalyses.videoAnalyses[analysisKey];
        setCurrentAnalysis(analysis);
        setSelectedVideo(analyzingVideo);
        setShowAnalysisResult(true);
      }
    }
    setAnalyzingVideo(null);
  }, [analyzingVideo, cachedAnalyses, fullReport, mode]);

  // Get videos by pitch type
  const getVideosByPitchType = (pitchType) => {
    return allVideos.filter(v => v.pitchType === pitchType);
  };

  // Get analysis for a video
  const getAnalysisForVideo = (video) => {
    if (!cachedAnalyses?.videoAnalyses) return null;
    const key = `${video.pitchType}_${video.angle}`;
    return cachedAnalyses.videoAnalyses[key];
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#E8B800] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="rounded-2xl overflow-hidden bg-black">
      {/* Matrix Animation Overlay */}
      <AnimatePresence>
        {analyzingVideo && (
          <MatrixAnalysisAnimation
            duration={11000}
            onComplete={handleAnalysisComplete}
            videoName={`${analyzingVideo.pitchType} - ${analyzingVideo.angleLabel}`}
          />
        )}
      </AnimatePresence>

      {/* Header */}
      <div className="p-6 border-b border-[#1A1A1A]">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-[14px] flex items-center justify-center ${
              mode === 'moe'
                ? 'bg-gradient-to-br from-red-500 to-[#C49B00]'
                : 'bg-gradient-to-br from-[#E8B800] to-[#C49B00]'
            }`}>
              <FolderOpen className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-[#F5F5F5]">
                {mode === 'moe' ? 'Advanced Scouting (MoE Vid Analyzer)' : 'Training Video Library'}
              </h2>
              <p className="text-sm text-[#999999]">
                {mode === 'moe'
                  ? `${allVideos.length} videos • Generates comprehensive intelligence report`
                  : `${allVideos.length} pre-analyzed videos ready for instant playback`
                }
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Database className="w-4 h-4 text-green-400" />
            <span className="text-sm text-green-400">
              {mode === 'moe' ? 'MoE Ready' : 'Cached'}
            </span>
          </div>
        </div>

        {/* Threat Assessment Summary */}
        {cachedAnalyses?.threatAssessment && (
          <div className="mt-4 p-4 rounded-[14px] bg-red-500/10 border border-red-500/30">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0" />
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-bold text-red-400">
                    {cachedAnalyses.threatAssessment.level} THREAT
                  </span>
                  <span className={`px-2 py-0.5 rounded text-xs font-bold bg-red-500/20 text-red-400`}>
                    {Math.round(cachedAnalyses.threatAssessment.tippingRate * 100)}% Tipping Rate
                  </span>
                </div>
                <p className="text-sm mt-1 text-[#999999]">
                  {cachedAnalyses.threatAssessment.quickRead}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Folder Structure */}
          <div className="lg:col-span-2 space-y-4">
            {PITCH_TYPES.map((pitchType) => (
              <div
                key={pitchType}
                className="rounded-[14px] overflow-hidden bg-[#0A0A0A]"
              >
                {/* Folder Header */}
                <button
                  onClick={() => setExpandedPitchType(expandedPitchType === pitchType ? null : pitchType)}
                  className="w-full p-4 flex items-center justify-between hover:bg-[rgba(255,255,255,0.04)] transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center bg-gradient-to-r ${PITCH_COLORS[pitchType].bg}`}>
                      <FolderOpen className="w-4 h-4 text-white" />
                    </div>
                    <span className="font-medium text-[#F5F5F5]">
                      {pitchType}
                    </span>
                    <span className="text-sm text-[#999999]">
                      ({getVideosByPitchType(pitchType).length} videos)
                    </span>
                  </div>
                  <ChevronRight className={`w-5 h-5 transition-transform ${
                    expandedPitchType === pitchType ? 'rotate-90' : ''
                  } text-[#999999]`} />
                </button>

                {/* Videos Grid */}
                <AnimatePresence>
                  {expandedPitchType === pitchType && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="overflow-hidden"
                    >
                      <div className={`p-4 pt-0 grid grid-cols-2 md:grid-cols-4 gap-3`}>
                        {getVideosByPitchType(pitchType).map((video) => (
                          <VideoCard
                            key={video.id}
                            video={video}
                            analysis={getAnalysisForVideo(video)}
                            isSelected={selectedVideo?.id === video.id}
                            onSelect={setSelectedVideo}
                            onAnalyze={handleAnalyze}
                          />
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>

          {/* Analysis Panel */}
          <div className="lg:col-span-1">
            <AnimatePresence mode="wait">
              {showAnalysisResult && selectedVideo && currentAnalysis ? (
                mode === 'moe' ? (
                  <WakeForestReportPanel
                    report={currentAnalysis}
                    onClose={() => setShowAnalysisResult(false)}
                  />
                ) : (
                  <AnalysisResultPanel
                    video={selectedVideo}
                    analysis={currentAnalysis}
                    onClose={() => setShowAnalysisResult(false)}
                  />
                )
              ) : (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="p-6 rounded-[14px] text-center bg-[#0A0A0A] border border-[#1A1A1A]"
                >
                  <Video className="w-12 h-12 mx-auto mb-4 text-[#333333]" />
                  <h3 className="font-medium text-[#F5F5F5]">
                    {mode === 'moe' ? 'MoE Video Analysis' : 'Select a Video'}
                  </h3>
                  <p className="text-sm mt-1 text-[#999999]">
                    {mode === 'moe'
                      ? 'Click "Analyze" on any video to generate comprehensive pitcher intelligence report'
                      : 'Click "Analyze" on any video to view pre-generated analysis results'
                    }
                  </p>
                  <div className="mt-4 flex items-center justify-center gap-2 text-xs text-[#555555]">
                    <Clock className="w-3 h-3" />
                    Analysis displays in ~11 seconds
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TrainingVideoLibrary;
